import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from './../../environments/environment';
import { User } from './../models/user';
import { Species } from '../models/species';
import { Disease } from '../models/disease';
import { SurgicalOperation } from '../models/surgicalOperation';
import { Animal, AnimalVaccineValence, AnimalVaccine, AnimalDisease, AnimalSurgicalOperation } from './../models/animal';
import { AgendaEventReportDocument, AgendaEvent, AgendaEventDocument, AgendaEventDocumentType, AgendaEventType, AgendaEventStatut, AgendaEventReport } from './../models/agenda';
import { VaccineValence, VaccineLabel, VaccineConfiguration } from './../models/vaccine';
import { ComptaInvoice, ComptaInvoiceLine } from './../models/compta';
import { StorageService } from './storage.service'
import { AlertController } from '@ionic/angular';
import { NetworkService } from './network.service';
import { UserService } from './user.service';
import { DateUtilsService } from './date-utils.service';
import { DiseaseService } from './disease.service';
import { SurgicalOperationService } from './surgical-operation.service';
import { IndexedDbService } from './indexed-db.service';
import { blobToBase64 } from '../utils'
import { TranslateService } from '@ngx-translate/core';
import { AnimalService } from './animal.service';
import { PharmacyProduct } from '../models/pharmacy';
import { PharmacyService } from './pharmacy.service';

@Injectable({
  providedIn: 'root'
})
export class ComptaService {
  invoices: ComptaInvoice[] = [];

  constructor(
    private http: HttpClient,
    public storageService: StorageService,
    public networkService: NetworkService,
    private translate: TranslateService,
    private dateUtils: DateUtilsService,
    private diseaseService: DiseaseService,
    private surgicalOperationService: SurgicalOperationService,
    private animalService: AnimalService,
    private userService: UserService,
    public indexedDbService: IndexedDbService,
    public pharmacyService: PharmacyService,
    public alertController: AlertController) { }


    dbSaveInvoice(invoice: ComptaInvoice): Promise<ComptaInvoice> {
      return new Promise((resolve, reject) => {
        if (this.networkService.isOnline()) {
          let headers = new HttpHeaders({
              'Authorization': this.userService.getHttpHeaders()
          });
          let formData: { [key: string]: any } = {};
          formData["id"] = invoice.id;
          formData["date"] = invoice.date;
          formData["description"] = invoice.description;
          formData["amount"] = invoice.amount;
          formData["eventId"] = invoice.eventId;

          if(invoice.fileNewName) {
            formData['file'] = {
              'base64': invoice.fileBase64,
              'name': invoice.fileNewName,
            }
          }

          formData['lines'] = []
          for(var line of invoice.lines) {
            formData['lines'].push({
              'id': line.id,
              'amount': line.amount,
              'description': line.description,
              'animalIds': line.animals.map(a => a.id),
            })
          }

          formData['pharmacyProductIds'] = []
          for(var pharmacyProduct of invoice.pharmacyProducts) {
            formData['pharmacyProductIds'].push(pharmacyProduct.id)
          }

          this.http.post<any[]>(environment.API_URL + 'compta/ws_private/SaveInvoice/', formData, {headers : headers}).subscribe({
            next: (response: { [key: string]: any }) => {
              if(!invoice.id) {
                this.invoices.push(invoice);
                this.invoices.sort((a, b) => (a.date || '').localeCompare(b.date || ''));
              }
              invoice.id = response['id']
              invoice.file = response['file']
              resolve(invoice);
            },
            error: async (error) => {
              reject(error);
            }
          });
        } else {
          this.indexedDbService.addItem({'obj': invoice, 'action': 'save'}).subscribe(() => {});
          resolve(invoice)
        }
      });
    }

    dbGetInvoicesOfAnimal(animalId: number): Promise<ComptaInvoice[] | null> {
      return new Promise((resolve) => {
        if (this.networkService.isOnline()) {
          let headers = new HttpHeaders({
              'Authorization': this.userService.getHttpHeaders()
          });
          this.http.get<any[]>(environment.API_URL + 'compta/ws_private/' + animalId + '/GetInvoicesOfAnimal/', {headers : headers}).subscribe(async (response: { [key: string]: any }) => {
            var invoices: ComptaInvoice[] =[]
  
            for(let b of response['invoices']) {
              var invoice = this.invoices.find(a => a.id == b['id'])
              if(!invoice){
                invoice = new ComptaInvoice()
                this.invoices.push(invoice);
              }

              invoice.id = b['id']
              invoice.amount = b['amount']
              invoice.userId = b['userId']
              invoice.date = b['date']
              invoice.description = b['description']
              invoice.file = b['file']

              if(b['event']) {
                invoice.eventId = b['event']['id']
                invoice.eventName = b['event']['name']
                invoice.eventDate = b['event']['date']
              }

              for(var line of b['lines']) {
                var l = invoice.lines.find(b => b.id == line['id'])
                if(!l){
                  l = new ComptaInvoiceLine()
                  invoice.lines.push(l);
                }
                l.id = line['id']
                l.amount = line['amount']
                l.description = line['description']
                l.animals = []
                for(var animal of line['animals']) {
                  var a = this.animalService.animals.find(b => b.id == animal['id'])
                  if(!a){
                    a = new Animal()
                    a.id = animal['id']
                    a.name = animal['name']
                  }
                  l.animals.push(a)
                }
              }

              invoices.push(invoice);
            }

            resolve(invoices);
          },
          () => {
            resolve(this.invoices.filter(a => a.lines.filter(l => l.animals.filter(a => a.id == animalId).length > 0).length > 0));
          })
        } else {
          resolve(this.invoices.filter(a => a.lines.filter(l => l.animals.filter(a => a.id == animalId).length > 0).length > 0));
        }
      });
    }

    dbGetInvoicesOfUser(userId: number): Promise<ComptaInvoice[] | null> {
      return new Promise((resolve) => {
        if (this.networkService.isOnline()) {
          let headers = new HttpHeaders({
              'Authorization': this.userService.getHttpHeaders()
          });
          this.http.get<any[]>(environment.API_URL + 'compta/ws_private/' + userId + '/GetInvoicesOfUser/', {headers : headers}).subscribe(async (response: { [key: string]: any }) => {
            var invoices: ComptaInvoice[] =[]
  
            for(let b of response['invoices']) {
              var invoice = this.invoices.find(a => a.id == b['id'])
              if(!invoice){
                invoice = new ComptaInvoice()
                this.invoices.push(invoice);
              }

              invoice.id = b['id']
              invoice.amount = b['amount']
              invoice.userId = b['userId']
              invoice.date = b['date']
              invoice.description = b['description']
              invoice.file = b['file']

              if(b['event']) {
                invoice.eventId = b['event']['id']
                invoice.eventName = b['event']['name']
                invoice.eventDate = b['event']['date']
              }

              invoice.pharmacyProducts = []
              for(var pharmacyProduct of b['pharmacyProducts']) {
                var p = this.pharmacyService.pharmacyProducts.find(b => b.id == pharmacyProduct['id'])
                if(!p){
                  p = new PharmacyProduct()
                  this.pharmacyService.pharmacyProducts.push(p)
                }
                p.id = pharmacyProduct['id']
                p.name = pharmacyProduct['name']
                invoice.pharmacyProducts.push(p)
              }

              invoice.lines = []
              for(var line of b['lines']) {
                var l = invoice.lines.find(b => b.id == line['id'])
                if(!l){
                  l = new ComptaInvoiceLine()
                  invoice.lines.push(l);
                }
                l.id = line['id']
                l.amount = line['amount']
                l.description = line['description']
                l.animals = []
                for(var animal of line['animals']) {
                  var a = this.animalService.animals.find(b => b.id == animal['id'])
                  if(!a){
                    a = new Animal()
                    this.animalService.animals.push(a)
                  }
                  a.id = animal['id']
                  a.name = animal['name']
                  l.animals.push(a)
                }
              }

              invoices.push(invoice);
            }

            resolve(invoices);
          },
          () => {
            resolve(this.invoices.filter(a => a.lines.filter(l => l.animals.filter(a => a.id == userId).length > 0).length > 0));
          })
        } else {
          resolve(this.invoices.filter(a => a.lines.filter(l => l.animals.filter(a => a.id == userId).length > 0).length > 0));
        }
      });
    }

    dbDeleteInvoice(invoice: ComptaInvoice): Promise<any> {
      return new Promise((resolve, reject) => {
        if (this.networkService.isOnline()) {
          let headers = new HttpHeaders({
              'Authorization': this.userService.getHttpHeaders()
          });
          let formData: { [key: string]: any } = {};
          formData["id"] = invoice.id;
          
          this.http.post<any[]>(environment.API_URL + 'compta/ws_private/DeleteInvoice/', formData, {headers : headers}).subscribe({
            next: (response: { [key: string]: any }) => {
              this.invoices = this.invoices.filter(a => a.id != invoice.id);
              resolve(invoice);
            },
            error: async (error) => {
              reject(error);
            }
          });
        } else {
          this.indexedDbService.addItem({'obj': invoice, 'action': 'delete'}).subscribe(() => {});
          resolve(invoice)
        }
      });
    }
}


